/**
 * Beschluss-Sammlung — Phase 2 (§ 24 Abs. 7 WEG).
 *
 * Zeigt die fortlaufend nummerierten Beschlüsse eines WEG-Objekts mit:
 *   - Nr., Datum, Titel/Wortlaut
 *   - Ergebnis (angenommen/abgelehnt/vertagt)
 *   - Anfechtungsfrist (30 Tage ab published_at/resolution_date)
 *   - Anfechtungs-Status, Ungültigkeit-Vermerk (§ 24 Abs. 7 Satz 5)
 *
 * Bearbeiten via Dialog, Löschen soft (Nummer bleibt vergeben).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useConfirm } from "@/components/ConfirmDialog";
import { Gavel, Warning, CheckCircle, XCircle, Clock, PencilSimple, Trash, Plus, Info, FilePdf } from "@phosphor-icons/react";
import { openAuthenticatedPdf } from "@/lib/pdfUtils";

const OUTCOME_META = {
  angenommen:      { label: "Angenommen",     color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  abgelehnt:       { label: "Abgelehnt",      color: "bg-rose-100 text-rose-800",       icon: XCircle },
  vertagt:         { label: "Vertagt",        color: "bg-amber-100 text-amber-800",     icon: Clock },
  zurueckgezogen:  { label: "Zurückgezogen",  color: "bg-slate-100 text-slate-700",     icon: XCircle },
};
const TYPE_LABEL = {
  allstimmig: "Allstimmig", mehrheit: "Mehrheit",
  qualifiziert: "Qualifiziert", umlauf: "Umlaufbeschluss", sonstige: "Sonstiges",
};
const CHALLENGE_LABEL = {
  keine: "Keine Anfechtung",
  angefochten: "Angefochten",
  aufgehoben: "Gerichtlich aufgehoben",
  bestaetigt: "Gerichtlich bestätigt",
  erledigt: "Erledigt",
};

const empty = () => ({
  resolution_date: new Date().toISOString().slice(0, 10),
  title: "",
  text: "",
  resolution_type: "mehrheit",
  outcome: "angenommen",
  votes_yes: "",
  votes_no: "",
  votes_abstain: "",
  agenda_item: "",
  published_at: "",
  challenge_status: "keine",
  challenge_note: "",
  invalid: false,
  invalid_reason: "",
});

export default function BeschlussSammlung({ property }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // Resolution-Objekt oder null (new)
  const confirm = useConfirm();
  // 2026-08 (iter111) — Beschluss-Sammlung-Export (§ 26a WEG)
  const [exportOpen, setExportOpen] = useState(false);

  const load = () =>
    api.get(`/weg/${property.id}/resolutions`).then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, [property.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (form) => {
    const payload = {
      property_id: property.id,
      resolution_date: form.resolution_date,
      title: form.title.trim(),
      text: form.text.trim(),
      resolution_type: form.resolution_type,
      outcome: form.outcome,
      votes_yes: form.votes_yes === "" ? null : Number(form.votes_yes),
      votes_no: form.votes_no === "" ? null : Number(form.votes_no),
      votes_abstain: form.votes_abstain === "" ? null : Number(form.votes_abstain),
      agenda_item: form.agenda_item || null,
      published_at: form.published_at || null,
      challenge_status: form.challenge_status,
      challenge_note: form.challenge_note || null,
      invalid: !!form.invalid,
      invalid_reason: form.invalid_reason || null,
    };
    try {
      if (editing?.id) {
        await api.patch(`/weg/resolutions/${editing.id}`, payload);
        toast.success(`Beschluss Nr. ${editing.resolution_number} aktualisiert`);
      } else {
        const r = await api.post(`/weg/${property.id}/resolutions`, payload);
        toast.success(`Beschluss Nr. ${r.data.resolution_number} angelegt`);
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const onDelete = async (r) => {
    if (!(await confirm({
      title: `Beschluss Nr. ${r.resolution_number} löschen?`,
      description: "Der Beschluss wird aus der Anzeige entfernt, die Nummer bleibt jedoch für die Rechtssicherheit vergeben (§ 24 Abs. 7 WEG).",
      confirmLabel: "Löschen",
      danger: true,
    }))) return;
    await api.delete(`/weg/resolutions/${r.id}`);
    toast.success("Beschluss gelöscht");
    load();
  };

  return (
    <div className="space-y-4" data-testid="beschluss-page">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Gavel weight="duotone" className="text-emerald-700" /> Beschluss-Sammlung
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Fortlaufende Beschlussdokumentation nach § 24 Abs. 7 WEG. Nummerierung ist rechtssicher — auch gelöschte Beschlüsse belegen ihre Nummer weiter.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="beschluss-add-btn" onClick={() => setEditing(null)}>
              <Plus size={14} className="mr-1.5" /> Neuer Beschluss
            </Button>
          </DialogTrigger>
          <BeschlussDialog editing={editing} onSubmit={onSubmit} />
        </Dialog>
        <Button
          size="sm" variant="outline" className="ml-2 gap-1.5"
          onClick={() => setExportOpen(true)}
          data-testid="beschluss-export-btn"
          title="Beschluss-Sammlung eines Jahres als PDF exportieren (§ 26a WEG)"
        >
          <FilePdf size={14} /> Sammlung exportieren
        </Button>
      </div>
      <BeschlussExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        property={property}
        rows={rows}
      />

      {loading ? (
        <div className="text-sm text-slate-500">Lade …</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center bg-slate-50" data-testid="beschluss-empty">
          <Gavel size={36} className="mx-auto text-slate-400 mb-2" />
          <div className="text-sm text-slate-600">Noch keine Beschlüsse in der Sammlung.</div>
          <div className="text-xs text-slate-500 mt-1">Neuer Beschluss startet die fortlaufende Nummerierung.</div>
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => {
            const outcome = OUTCOME_META[r.outcome] || OUTCOME_META.angenommen;
            const OutcomeIcon = outcome.icon;
            return (
              <li
                key={r.id}
                data-testid={`beschluss-row-${r.resolution_number}`}
                className={`rounded-lg border ${r.invalid ? "border-rose-300 bg-rose-50/40" : "border-slate-200 bg-white"} p-4`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs text-slate-500 font-semibold">Nr. {r.resolution_number}</span>
                      <span className="text-xs text-slate-500">{r.resolution_date}</span>
                      {r.agenda_item && (
                        <Badge variant="outline" className="text-[10px]">{r.agenda_item}</Badge>
                      )}
                      <Badge className={`text-[10px] ${outcome.color}`}>
                        <OutcomeIcon size={10} className="inline mr-0.5" weight="fill" />
                        {outcome.label}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">{TYPE_LABEL[r.resolution_type] || r.resolution_type}</Badge>
                      {r.invalid && (
                        <Badge className="bg-rose-100 text-rose-800 text-[10px]">
                          <Warning size={10} weight="fill" className="inline mr-0.5" /> Ungültig
                        </Badge>
                      )}
                    </div>
                    <div className="font-medium text-slate-900">{r.title}</div>
                    <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{r.text}</p>
                    {(r.votes_yes !== null || r.votes_no !== null || r.votes_abstain !== null) && (
                      <div className="text-xs text-slate-500 mt-2">
                        Stimmen — Ja: <b>{r.votes_yes ?? 0}</b> · Nein: <b>{r.votes_no ?? 0}</b> · Enthaltung: <b>{r.votes_abstain ?? 0}</b>
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-2 flex-wrap">
                      {r.challenge_deadline && (
                        <span className="inline-flex items-center gap-1">
                          <Clock size={11} />
                          Anfechtungsfrist bis <b>{r.challenge_deadline}</b>
                          {r.challenge_period_open && (
                            <span className="ml-1 text-amber-700">(noch offen)</span>
                          )}
                        </span>
                      )}
                      {r.challenge_status !== "keine" && (
                        <Badge className="bg-amber-100 text-amber-800 text-[10px]">
                          {CHALLENGE_LABEL[r.challenge_status]}
                        </Badge>
                      )}
                    </div>
                    {r.invalid && r.invalid_reason && (
                      <div className="mt-2 text-xs text-rose-700 border-l-2 border-rose-400 pl-2">
                        <Info size={11} className="inline mr-1" /> {r.invalid_reason}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost" size="sm"
                      data-testid={`beschluss-edit-${r.resolution_number}`}
                      onClick={() => { setEditing(r); setOpen(true); }}
                    >
                      <PencilSimple size={13} />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      data-testid={`beschluss-delete-${r.resolution_number}`}
                      onClick={() => onDelete(r)}
                    >
                      <Trash size={13} className="text-rose-600" />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function BeschlussDialog({ editing, onSubmit }) {
  const [form, setForm] = useState(empty());
  useEffect(() => {
    if (editing?.id) {
      setForm({
        resolution_date: editing.resolution_date || "",
        title: editing.title || "",
        text: editing.text || "",
        resolution_type: editing.resolution_type || "mehrheit",
        outcome: editing.outcome || "angenommen",
        votes_yes: editing.votes_yes ?? "",
        votes_no: editing.votes_no ?? "",
        votes_abstain: editing.votes_abstain ?? "",
        agenda_item: editing.agenda_item || "",
        published_at: editing.published_at || "",
        challenge_status: editing.challenge_status || "keine",
        challenge_note: editing.challenge_note || "",
        invalid: !!editing.invalid,
        invalid_reason: editing.invalid_reason || "",
      });
    } else {
      setForm(empty());
    }
  }, [editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="beschluss-dialog">
      <DialogHeader>
        <DialogTitle>{editing?.id ? `Beschluss Nr. ${editing.resolution_number} bearbeiten` : "Neuer Beschluss"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-slate-600">
            Beschluss-Datum *
            <Input type="date" value={form.resolution_date} onChange={(e) => set("resolution_date", e.target.value)} data-testid="beschluss-date" />
          </label>
          <label className="text-xs text-slate-600">
            Verkündet am (Fristbeginn)
            <Input type="date" value={form.published_at} onChange={(e) => set("published_at", e.target.value)} data-testid="beschluss-published" />
          </label>
        </div>

        <label className="text-xs text-slate-600 block">
          Titel *
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="z. B. Genehmigung Wirtschaftsplan 2026" data-testid="beschluss-title" />
        </label>

        <label className="text-xs text-slate-600 block">
          Wortlaut *
          <Textarea value={form.text} onChange={(e) => set("text", e.target.value)} rows={5} placeholder="Vollständiger Wortlaut des Beschlusses …" data-testid="beschluss-text" />
        </label>

        <div className="grid grid-cols-3 gap-3">
          <label className="text-xs text-slate-600">
            TOP
            <Input value={form.agenda_item} onChange={(e) => set("agenda_item", e.target.value)} placeholder="TOP 4" />
          </label>
          <label className="text-xs text-slate-600">
            Art
            <Select value={form.resolution_type} onValueChange={(v) => set("resolution_type", v)}>
              <SelectTrigger data-testid="beschluss-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="text-xs text-slate-600">
            Ergebnis
            <Select value={form.outcome} onValueChange={(v) => set("outcome", v)}>
              <SelectTrigger data-testid="beschluss-outcome"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(OUTCOME_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label className="text-xs text-slate-600">Ja-Stimmen<Input type="number" min="0" value={form.votes_yes} onChange={(e) => set("votes_yes", e.target.value)} data-testid="beschluss-votes-yes" /></label>
          <label className="text-xs text-slate-600">Nein-Stimmen<Input type="number" min="0" value={form.votes_no} onChange={(e) => set("votes_no", e.target.value)} data-testid="beschluss-votes-no" /></label>
          <label className="text-xs text-slate-600">Enthaltungen<Input type="number" min="0" value={form.votes_abstain} onChange={(e) => set("votes_abstain", e.target.value)} /></label>
        </div>

        <div className="border-t border-slate-200 pt-3 space-y-3">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Anfechtung / Ungültigkeit (§ 24 Abs. 7 Satz 5)</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-600">
              Anfechtungs-Status
              <Select value={form.challenge_status} onValueChange={(v) => set("challenge_status", v)}>
                <SelectTrigger data-testid="beschluss-challenge-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CHALLENGE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-700 pt-6">
              <input type="checkbox" checked={form.invalid} onChange={(e) => set("invalid", e.target.checked)} data-testid="beschluss-invalid" />
              Beschluss ist ungültig
            </label>
          </div>
          {form.challenge_status !== "keine" && (
            <label className="text-xs text-slate-600 block">
              Notiz zur Anfechtung
              <Textarea value={form.challenge_note} onChange={(e) => set("challenge_note", e.target.value)} rows={2} placeholder="Az., Datum, Urteilstenor …" />
            </label>
          )}
          {form.invalid && (
            <label className="text-xs text-slate-600 block">
              Grund der Ungültigkeit *
              <Textarea value={form.invalid_reason} onChange={(e) => set("invalid_reason", e.target.value)} rows={2} placeholder="Formfehler, verstößt gegen …" data-testid="beschluss-invalid-reason" />
            </label>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button
          onClick={() => onSubmit(form)}
          disabled={!form.title.trim() || !form.text.trim() || !form.resolution_date || (form.invalid && !form.invalid_reason.trim())}
          data-testid="beschluss-save-btn"
        >
          {editing?.id ? "Aktualisieren" : "Beschluss anlegen"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}



// 2026-08 (iter111) — Beschluss-Sammlung-Export nach § 26a WEG
function BeschlussExportDialog({ open, onOpenChange, property, rows }) {
  const yearsFromRows = Array.from(new Set(
    (rows || [])
      .map((r) => (r.resolution_date || "").slice(0, 4))
      .filter((y) => y && /^\d{4}$/.test(y)),
  )).sort().reverse();
  const currentYear = new Date().getFullYear();
  const availableYears = yearsFromRows.length ? yearsFromRows : [String(currentYear)];
  const [year, setYear] = useState(availableYears[0]);
  const [includeRejected, setIncludeRejected] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setYear(availableYears[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const doExport = async () => {
    setLoading(true);
    try {
      await openAuthenticatedPdf(
        `/weg/pdf/beschluss-sammlung/${property.id}?year=${year}&include_rejected=${includeRejected}`,
      );
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || "Export fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="beschluss-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilePdf size={18} className="text-emerald-700" />
            Beschluss-Sammlung exportieren
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-2.5">
            Nach <b>§ 26a WEG</b> ist die Beschluss-Sammlung neuen Eigentümern
            beim Erwerb einer Einheit auszuhändigen. Erzeugt eine
            chronologische PDF-Übersicht aller Beschlüsse des gewählten Jahres.
          </div>
          <label className="text-xs text-slate-700 block">
            Jahr
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="mt-1" data-testid="beschluss-export-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={includeRejected}
              onChange={(e) => setIncludeRejected(e.target.checked)}
              className="mt-0.5"
              data-testid="beschluss-export-include-rejected"
            />
            <span>
              <b>Auch abgelehnte / vertagte Beschlüsse aufnehmen</b>
              <div className="text-[10px] text-slate-500 mt-0.5">
                Default: nur angenommene Beschlüsse. Für interne Zwecke ggf.
                alle Beschlüsse inkl. Status-Kennzeichnung einschließen.
              </div>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            onClick={doExport}
            disabled={loading || !year}
            className="bg-emerald-700 hover:bg-emerald-800"
            data-testid="beschluss-export-download"
          >
            <FilePdf size={14} className="mr-1.5" />
            {loading ? "Erzeuge PDF…" : "PDF herunterladen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
